import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, ElementRef} from '@angular/core';
import {MapService} from '../../shared/map/map.service';
import {LinkingCrudService} from '../linking.service';
import {Linking} from '../linking.model';
import {MappingService} from '../mapping.service';
import {IMappingDefinitions, IMappingDetails, IRegoMappingDefinition, IRegoFeatureDefinition} from '../mapping';

import {BaseComponent} from '../../components/general/base/base.component';
import {Observable} from 'rxjs';
import {debounceTime, map, distinctUntilChanged, withLatestFrom} from 'rxjs/operators';
import { ILayer } from '../../shared/map/layer/layer.type';
import { AuthService } from '~app/auth/auth.service';
import { CanvasLayer } from '~app/shared/map/layer/layer.model';

@Component({
  selector: 'qm-linking-valmet',
  templateUrl: './linking-valmet.component.html',
  styleUrls: ['./linking-valmet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LinkingValmetComponent extends BaseComponent<Linking> implements OnInit {
  @ViewChild('searchTerm') searchTerm: ElementRef;

  public mappingDataValmet: IMappingDefinitions = {};
  public mappingDataValmetList: IMappingDetails[] = [];

  public editLink: string;
  public currentLink: IRegoFeatureDefinition = null;
  public mapObjectList: IRegoMappingDefinition;
  public linkingListValmet: Linking[] = [];
  public selectedLayer: string;
  public regoId: string;
  public loading: boolean;
  /**
   * Paging
   */
  public page: number = 1;
  public pageSize: number = 50;
  public total: number = 0;

  public showOnlyLinkedItems: boolean = false;
  public searchMapObjects = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      withLatestFrom(this.mapService.layers$.pipe(
        map(layers => layers.filter((layer: ILayer) => layer.linkable).map(layer => layer.id)),
      )),
      map(([term, linkableLayerIds]) => {
        if (!term) {
          return [];
        }
        term = term.toLowerCase();
        return this.mapObjectList.layers
          .filter(layer => linkableLayerIds.includes(layer.name))
          .flatMap(layer => layer.features)
          .filter(c => ('' + c.id).includes(term) || c.title && `${c.title}`.toLowerCase().includes(term))
          .slice(0, 20);
      })
    )

    public searchSource = (text$: Observable<string>) =>
      text$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        map(term => {
          if (!term) {
            this.resetSourceList();
          }
          term = term.toLowerCase();
          //// always filter with search term
          this.mappingDataValmetList =
            this.mappingDataValmet.sourceSystemList.filter(item =>
              item.deviceId.toLowerCase().includes(term));
          //// if checkbox ticked, some further filtering
          if (this.showOnlyLinkedItems) {
            this.mappingDataValmetList = this.mappingDataValmetList.filter(item => !!this.linkingListValmet[item.deviceId]);
          }
          this.total = this.mappingDataValmetList.length;
          this.page = 1;
          this.changeDetectorRef.markForCheck();
      })
    )

   constructor(
      private readonly linkingCrudService: LinkingCrudService,
      private readonly mappingService: MappingService,
      private readonly mapService: MapService,
      private readonly authService: AuthService,
      private readonly changeDetectorRef: ChangeDetectorRef) {
    super();
    this.name = 'LinkingValmetComponent';
  }

  ngOnInit() {
    this.loadList();
  }

  private async loadList() {
    this.linkingListValmet = [];
    this.loading = true;

    //// mapping list from RB
    try {
      this.mappingDataValmet = await this.mappingService.getValmetMappingList();
      this.resetSourceList();
      this.total = this.mappingDataValmetList.length;
    } catch (error) {
      //// error.status may be for example 404, which indicates that list is not available or present for tenant
      //// this should be prevented, at least mostly, with correct user rights.
      console.log(error);
      this.loading = false;
      return;
    }

    //// LINKING LIST FROM RB
    const list = await this.linkingCrudService.filter({sourceSystem: 'valmet'});

    if (list.isOk) {
      for (const row of list.data) {
        this.linkingListValmet[row.sourceId] = row;
      }
    }

    this.mapObjectList = await this.mappingService.getRegoMappingList();
    this.loading = false;
    this.changeDetectorRef.markForCheck();
  }

  public onDisplayLinkedItemsChange(event: boolean) {
    //// want to see only already linked items
    this.showOnlyLinkedItems = event;
    this.filterLinked();
  }

  public setEditLink(asset: string) {
    this.editLink = asset;
    this.currentLink = null;
  }

  public async saveLink() {
    if (this.regoId || this.currentLink) {
      const m = new Linking();
      if (this.regoId) {
        m.regoId = this.regoId;
      } else {
        m.regoId = `${this.currentLink.id}`;
      }
      m.sourceId = this.editLink;
      m.sourceSystem = this.mappingDataValmet.sourceSystem;
      this.linkingListValmet[this.editLink] = m;
      await this.linkingCrudService.create(m);
      this.currentLink = null;
      this.editLink = '';
      this.changeDetectorRef.markForCheck();
      this.loadList();
    }
  }

  public getMapObjectTitle(obj: IRegoFeatureDefinition): string {
    return obj.id.toString() === obj.title ? obj.title : `${obj.id} ${obj.title}`;
  }

  public getTitle(id: number): string {
    const featurestring = this.mapObjectList.layers.map(layer => {
      const feat = layer.features.find(f => `${f.id}` === `${id}`);
      if(!feat) {
        return undefined;
      }
      const featLabel = feat.id.toString() === feat.title ? feat.title : `${feat.id} ${feat.title}`;
      return `${layer.name} / ${featLabel}`;
    }).find(val => val !== undefined);
    return featurestring ? featurestring : `${id}`;
  }

  public async unLink() {
    const link = this.linkingListValmet[this.editLink];
    await this.linkingCrudService.remove(link);
    this.editLink = '';
    this.loadList();
  }

  private filterLinked() {
    if (this.showOnlyLinkedItems) {
      this.mappingDataValmetList = this.mappingDataValmetList.filter(item => {
        return !!this.linkingListValmet[item.deviceId];
      });
        this.total = this.mappingDataValmetList.length;
    } else {
      //// uhm, avoid unnecessary traffic
      this.loadList();
    }
  }

  private resetSourceList() {
    this.showOnlyLinkedItems = false;
    this.searchTerm.nativeElement.value = '';
    this.mappingDataValmetList = this.mappingDataValmet.sourceSystemList;
  }
}
